import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle } from '@tabler/icons-react';
import type {
  Expense,
  PaymentMethod,
  PaymentRequirement,
} from '@pta/types';
import { createExpense, updateExpense } from '../api/expenses';
import { ApiError } from '../api/client';
import { toIsoDate } from '../util/format';
import { useMoneyFormatter } from '../util/money';

interface ExpenseFormModalProps {
  opened: boolean;
  onClose: () => void;
  groupId: number;
  /** Csoport primary currency-je (NumberInput label fallback). */
  currency: string;
  requirements: PaymentRequirement[];
  expense?: Expense | null;
  onSaved: () => void;
}

const PAYMENT_METHODS: Array<{ value: PaymentMethod; label: string }> = [
  { value: 'cash', label: 'Készpénz' },
  { value: 'bank_transfer', label: 'Banki átutalás' },
  { value: 'card', label: 'Kártya' },
  { value: 'escrow', label: 'Letét' },
];

/** Teljes szélességű grid-cella (mobilon és desktopon is full-width). */
const fullSpan = { gridColumn: '1 / -1' };

/**
 * F2f — Expense create/edit modal (FE).
 *
 * Layout: mobilon 1 oszlop, desktopon (sm+) 2 oszlop. Hosszú/multi-line mezők
 * `gridColumn: '1 / -1'`-tel teljes szélességűek.
 */
export function ExpenseFormModal({
  opened,
  onClose,
  groupId,
  currency,
  requirements,
  expense,
  onSaved,
}: ExpenseFormModalProps) {
  const { currencyLabel } = useMoneyFormatter();
  const isEdit = expense != null;

  const [requirementId, setRequirementId] = useState<string | null>(null);
  const [purpose, setPurpose] = useState('');
  const [amount, setAmount] = useState<number | ''>('');
  const [spentAt, setSpentAt] = useState<Date | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('bank_transfer');
  const [payeeName, setPayeeName] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!opened) return;
    if (isEdit && expense) {
      setRequirementId(expense.requirementId !== null ? String(expense.requirementId) : null);
      setPurpose(expense.purpose);
      setAmount(Number(expense.amount));
      setSpentAt(new Date(expense.spentAt));
      setPaymentMethod(expense.paymentMethod);
      setPayeeName(expense.payeeName ?? '');
      setInvoiceNumber(expense.invoiceNumber ?? '');
      setNotes(expense.notes ?? '');
    } else {
      setRequirementId(null);
      setPurpose('');
      setAmount('');
      setSpentAt(new Date());
      setPaymentMethod('bank_transfer');
      setPayeeName('');
      setInvoiceNumber('');
      setNotes('');
    }
  }, [opened, isEdit, expense]);

  const canSave =
    purpose.trim().length > 0 &&
    typeof amount === 'number' &&
    amount > 0 &&
    spentAt !== null &&
    !saving;

  const handleSave = async () => {
    if (!canSave || typeof amount !== 'number' || !spentAt) return;
    setSaving(true);
    try {
      const amountStr = amount.toFixed(2).replace(/\.00$/, '');
      const reqId = requirementId !== null ? Number(requirementId) : null;
      if (isEdit && expense) {
        await updateExpense(expense.id, {
          requirementId: reqId,
          purpose: purpose.trim(),
          amount: amountStr,
          spentAt: toIsoDate(spentAt),
          paymentMethod,
          payeeName: payeeName.trim() || null,
          invoiceNumber: invoiceNumber.trim() || null,
          notes: notes.trim() || null,
        });
        notifications.show({ color: 'green', message: 'Kiadás frissítve.' });
      } else {
        await createExpense(groupId, {
          requirementId: reqId,
          purpose: purpose.trim(),
          amount: amountStr,
          spentAt: toIsoDate(spentAt),
          paymentMethod,
          payeeName: payeeName.trim() || null,
          invoiceNumber: invoiceNumber.trim() || null,
          notes: notes.trim() || null,
        });
        notifications.show({ color: 'green', message: 'Kiadás rögzítve.' });
      }
      onSaved();
    } catch (e) {
      notifications.show({
        color: 'red',
        message: e instanceof ApiError ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  const requirementOptions = [
    { value: '', label: '— általános működési kiadás —' },
    ...requirements.map((r) => ({
      value: String(r.id),
      label: `#${r.id} — ${r.title} (${r.purpose})`,
    })),
  ];

  const labelForInput = currencyLabel || currency;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? 'Kiadás szerkesztése' : 'Új kiadás rögzítése'}
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      size="lg"
    >
      <Stack>
        <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
          <Text size="sm">
            Cél-kötött kiadás: válassz előírást. Általános működési kiadás
            esetén hagyd "általános"-on, és írd be a célt a Cél mezőbe (pl.
            "irodaszer", "banki költség").
          </Text>
        </Alert>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="sm">
          <Select
            style={fullSpan}
            label="Cél-kötés (opcionális előírás)"
            data={requirementOptions}
            value={requirementId ?? ''}
            onChange={(v) => setRequirementId(v && v.length > 0 ? v : null)}
            searchable
          />

          <TextInput
            style={fullSpan}
            label="Cél / megjelölés"
            description="Pl. buszbérlés, irodaszer, banki költség"
            value={purpose}
            onChange={(e) => setPurpose(e.currentTarget.value)}
            required
            maxLength={200}
          />

          <NumberInput
            label={`Összeg (${labelForInput})`}
            value={amount}
            onChange={(v) => setAmount(typeof v === 'number' ? v : '')}
            min={0}
            decimalScale={2}
            thousandSeparator=" "
            required
          />

          <DateInput
            label="Fizetés dátuma"
            value={spentAt}
            onChange={(v) => setSpentAt(v ? new Date(v) : null)}
            valueFormat="YYYY-MM-DD"
            required
          />

          <Select
            label="Fizetési mód"
            data={PAYMENT_METHODS}
            value={paymentMethod}
            onChange={(v) => setPaymentMethod((v as PaymentMethod) ?? 'bank_transfer')}
            allowDeselect={false}
            required
          />

          <TextInput
            label="Akinek fizettünk (opcionális)"
            value={payeeName}
            onChange={(e) => setPayeeName(e.currentTarget.value)}
            maxLength={200}
          />

          <TextInput
            style={fullSpan}
            label="Számla / nyugta száma (opcionális)"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.currentTarget.value)}
            maxLength={100}
          />

          <Textarea
            style={fullSpan}
            label="Megjegyzés (opcionális)"
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            autosize
            minRows={2}
            maxLength={2000}
          />
        </SimpleGrid>

        <Group justify="end">
          <Button variant="default" onClick={onClose} disabled={saving}>
            Mégse
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={!canSave}>
            {isEdit ? 'Mentés' : 'Rögzítés'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
